import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { format, subDays, startOfMonth, endOfMonth, subMonths, differenceInCalendarDays } from "date-fns";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useSalesInsights } from "@/hooks/useSalesInsights";
import {
  money,
  money2,
  pctOf,
  bucketTrend,
  acquisitionVerdict,
  weakestDays,
  buildPriorities,
  buildScorecard,
  type ScorecardStatus,
  sortByOrder,
  FREQUENCY_ORDER,
  FREQUENCY_LABELS,
  RECENCY_ORDER,
  RECENCY_LABELS,
  DOW_LABELS,
  WINBACK_SEGMENTS,
} from "@/utils/insightsUtils";
import { MetricCard } from "@/components/MetricCard";
import { chartColors, LADDER_COLORS, RECENCY_COLORS } from "@/lib/chartColors";
import { SortableHead } from "@/components/SortableHead";
import { useTableSort } from "@/hooks/useTableSort";
import { exportToCsv, downloadBlob } from "@/lib/exportCsv";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarIcon,
  Download,
  DollarSign,
  Receipt,
  Users,
  UserPlus,
  Percent,
  ShoppingBag,
  ArrowRight,
  Gauge,
  TrendingDown,
  TrendingUp,
  Target,
  CheckCircle2,
} from "lucide-react";

// Chart series colors + ladder/recency scales live in src/lib/chartColors.ts.
const { brandRed: RED, brandGreen: GREEN, brandYellow: YELLOW } = chartColors;

type PresetKey = "30d" | "90d" | "thisMonth" | "lastMonth" | "12m";

const PRESETS: { key: PresetKey; label: string; range: () => DateRange }[] = [
  { key: "30d", label: "Last 30 days", range: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { key: "90d", label: "Last 90 days", range: () => ({ from: subDays(new Date(), 89), to: new Date() }) },
  { key: "thisMonth", label: "This month", range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  {
    key: "lastMonth",
    label: "Last month",
    range: () => {
      const lm = subMonths(new Date(), 1);
      return { from: startOfMonth(lm), to: endOfMonth(lm) };
    },
  },
  { key: "12m", label: "Last 12 months", range: () => ({ from: subDays(new Date(), 364), to: new Date() }) },
];

const SectionCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-lg">{title}</CardTitle>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const ActionNote = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-4 rounded-lg border border-dashed border-status-caution bg-status-caution/10 px-4 py-3 text-sm">
    <span className="font-bold text-brand-red mr-1.5">→</span>
    {children}
  </div>
);

// Status ladder for the scorecard. Reds here are the `status-danger` token, not
// the action red: DESIGN.md reserves brand red for actions so a bad number never
// reads as a button.
const SCORE_DOT: Record<ScorecardStatus, string> = {
  good: "bg-brand-green",
  watch: "bg-status-caution",
  bad: "bg-status-danger",
  neutral: "bg-muted-foreground/40",
};

const SCORE_VALUE: Record<ScorecardStatus, string> = {
  good: "text-brand-green",
  watch: "text-status-warning",
  bad: "text-status-danger",
  neutral: "text-foreground",
};

const StatBox = ({ big, caption, accent = "red" }: { big: string; caption: React.ReactNode; accent?: "red" | "green" }) => (
  <div className="flex-1 min-w-[160px] rounded-lg border bg-muted p-4">
    <div className={`text-2xl font-extrabold ${accent === "red" ? "text-brand-red" : "text-brand-green"}`}>{big}</div>
    <div className="text-xs text-muted-foreground mt-1">{caption}</div>
  </div>
);

const Insights = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRange>(() => PRESETS[1].range()); // default: last 90 days
  const [activePreset, setActivePreset] = useState<PresetKey | null>("90d");
  const [pickerOpen, setPickerOpen] = useState(false);

  const from = range.from ?? subDays(new Date(), 89);
  const to = range.to ?? range.from ?? new Date();
  const rangeDays = differenceInCalendarDays(to, from) + 1;

  const { data, isLoading, error } = useSalesInsights(from, to);

  const trendBuckets = useMemo(() => (data ? bucketTrend(data.trend, rangeDays) : []), [data, rangeDays]);
  const verdict = useMemo(() => (data ? acquisitionVerdict(data.trend) : null), [data]);
  const plays = useMemo(() => (data ? buildPriorities(data) : []), [data]);
  const scorecard = useMemo(() => (data ? buildScorecard(data) : []), [data]);
  const {
    sort: pairSort,
    toggle: togglePairSort,
    sorted: sortedPairings,
  } = useTableSort(data?.pairings ?? [], { key: "times_paired", direction: "desc" });

  const ladder = useMemo(
    () =>
      data
        ? sortByOrder(data.ladder, (l) => l.frequency_segment, FREQUENCY_ORDER).map((l) => ({
            ...l,
            label: FREQUENCY_LABELS[l.frequency_segment] ?? l.frequency_segment,
          }))
        : [],
    [data]
  );
  const ladderRevenue = ladder.reduce((s, l) => s + Number(l.revenue), 0);

  const recency = useMemo(
    () =>
      data
        ? sortByOrder(data.recency, (r) => r.recency_segment, RECENCY_ORDER).map((r) => ({
            ...r,
            label: RECENCY_LABELS[r.recency_segment] ?? r.recency_segment,
          }))
        : [],
    [data]
  );
  const winbackValue = recency
    .filter((r) => WINBACK_SEGMENTS.includes(r.recency_segment))
    .reduce((s, r) => s + Number(r.lifetime_value), 0);

  const dowData = useMemo(
    () => (data ? [...data.dow].sort((a, b) => a.iso_dow - b.iso_dow).map((d) => ({ ...d, label: DOW_LABELS[d.iso_dow - 1] })) : []),
    [data]
  );
  const weak = data ? weakestDays(data.dow, 2).map((w) => DOW_LABELS[w.iso_dow - 1]) : [];

  const applyPreset = (key: PresetKey) => {
    const preset = PRESETS.find((p) => p.key === key)!;
    setRange(preset.range());
    setActivePreset(key);
  };

  // ---- Export for external analysis ----
  const periodStart = format(from, "yyyy-MM-dd");
  const periodEnd = format(to, "yyyy-MM-dd");
  const rangeStamp = `${periodStart}_to_${periodEnd}`;

  const exportSummaryCsv = () => {
    if (!data?.kpis) return;
    const d = data.discounts;
    exportToCsv(`insights-summary_${rangeStamp}.csv`, [
      {
        period_start: periodStart,
        period_end: periodEnd,
        ...data.kpis,
        discount_to_repeat_customers: d?.discount_to_repeat ?? "",
        avg_basket_discounted: d?.avg_basket_discounted ?? "",
        avg_basket_full_price: d?.avg_basket_full ?? "",
      },
    ]);
  };

  const exportSectionCsv = (name: string, rows: readonly object[]) => {
    if (rows.length === 0) return;
    exportToCsv(`insights-${name}_${rangeStamp}.csv`, rows);
  };

  const exportAllJson = () => {
    if (!data) return;
    const payload = {
      period: { start: periodStart, end: periodEnd },
      exported_at: new Date().toISOString(),
      ...data,
    };
    downloadBlob(
      `insights_${rangeStamp}.json`,
      new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    );
  };

  const kpis = data?.kpis;
  const discounts = data?.discounts;
  const members = data?.loyalty.find((l) => l.is_loyalty);
  const nonMembers = data?.loyalty.find((l) => !l.is_loyalty);

  return (
    <div className="pb-10">
      {/* Header + date controls */}
      <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Insights</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            What your sales data says, and exactly what to do about it
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={activePreset === p.key ? "default" : "outline"}
              className={activePreset === p.key ? "bg-brand-red hover:bg-brand-red/90 text-brand-white" : ""}
              onClick={() => applyPreset(p.key)}
            >
              {p.label}
            </Button>
          ))}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant={activePreset === null ? "default" : "outline"}
                className={`flex items-center gap-2 ${activePreset === null ? "bg-brand-red hover:bg-brand-red/90 text-brand-white" : ""}`}
              >
                <CalendarIcon className="w-4 h-4" />
                {format(from, "MMM d, yyyy")} – {format(to, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                defaultMonth={from}
                selected={range}
                numberOfMonths={2}
                onSelect={(r) => {
                  if (r?.from) {
                    setRange(r);
                    setActivePreset(null);
                    if (r.to) setPickerOpen(false);
                  }
                }}
              />
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                disabled={!data}
                className="bg-brand-green hover:bg-brand-green/90 text-brand-white flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                Export {format(from, "MMM d")} – {format(to, "MMM d, yyyy")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportAllJson}>
                Everything (JSON)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={!data?.kpis} onClick={exportSummaryCsv}>
                KPI summary (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={scorecard.length === 0}
                onClick={() =>
                  exportSectionCsv(
                    "scorecard",
                    scorecard.map((t) => ({
                      metric: t.title,
                      value: t.value,
                      status: t.status,
                      detail: t.detail,
                    }))
                  )
                }
              >
                Owner scorecard (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.trend.length}
                onClick={() => exportSectionCsv("daily-trend", data?.trend ?? [])}
              >
                Daily revenue &amp; customers (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.channels.length}
                onClick={() => exportSectionCsv("channels", data?.channels ?? [])}
              >
                Channels (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.dow.length}
                onClick={() =>
                  exportSectionCsv(
                    "day-of-week",
                    (data?.dow ?? []).map((d) => ({ day: DOW_LABELS[d.iso_dow - 1], ...d }))
                  )
                }
              >
                Revenue by day of week (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.pairings.length}
                onClick={() => exportSectionCsv("item-pairings", data?.pairings ?? [])}
              >
                Item pairings (CSV)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!data?.ladder.length}
                onClick={() => exportSectionCsv("value-ladder", data?.ladder ?? [])}
              >
                Customer value ladder (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.recency.length}
                onClick={() => exportSectionCsv("recency-segments", data?.recency ?? [])}
              >
                Win-back segments (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!data?.loyalty.length}
                onClick={() => exportSectionCsv("loyalty", data?.loyalty ?? [])}
              >
                Loyalty comparison (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-status-danger/30 bg-status-danger/10 px-4 py-3 text-sm text-status-danger">
          Couldn't load insights: {error instanceof Error ? error.message : "unknown error"}
        </div>
      )}

      {/* OWNER SCORECARD */}
      {!isLoading && scorecard.length > 0 && (
        <Card className="mb-6 border-brand-red/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Gauge className="w-5 h-5 text-brand-red" />
              Owner scorecard: check these every Monday
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              The four numbers that most directly grow sales: grow the 2nd-order rate, shrink
              discounts to regulars, drain the win-back pipeline, keep VIPs active.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {scorecard.map((tile) => (
                <div key={tile.key} className="rounded-lg border bg-muted p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-full flex-none ${SCORE_DOT[tile.status]}`} />
                    <span className="text-xs font-bold uppercase tracking-wide text-brand-gray">
                      {tile.title}
                    </span>
                  </div>
                  <div className={`text-2xl font-extrabold ${SCORE_VALUE[tile.status]}`}>{tile.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">{tile.detail}</p>
                  <p className="text-xs text-muted-foreground/80 italic mt-1.5">{tile.why}</p>
                  <button
                    onClick={() => navigate(tile.actionTo)}
                    className="mt-auto pt-2 text-xs font-semibold text-brand-green hover:underline flex items-center gap-1 self-start"
                  >
                    {tile.actionLabel}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI strip. Icon color states each number's meaning per DESIGN.md:
          success (money in), info (counts / new), neutral (context), warning
          (a cost to watch). Brand red stays reserved for actions, not metrics. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard icon={DollarSign} value={isLoading || !kpis ? "…" : money(kpis.revenue)} label="Revenue" iconColor="text-brand-green" iconBgColor="bg-status-success/10" />
        <MetricCard icon={Receipt} value={isLoading || !kpis ? "…" : Number(kpis.paid_orders)} label="Paid orders" iconColor="text-status-info" iconBgColor="bg-status-info/10" />
        <MetricCard icon={Users} value={isLoading || !kpis ? "…" : Number(kpis.customers)} label="Customers" iconColor="text-brand-gray" iconBgColor="bg-muted" />
        <MetricCard icon={ShoppingBag} value={isLoading || !kpis ? "…" : money2(kpis.avg_order_value)} label="Avg order" iconColor="text-brand-green" iconBgColor="bg-status-success/10" />
        <MetricCard icon={UserPlus} value={isLoading || !kpis ? "…" : Number(kpis.new_customers)} label="New customers" iconColor="text-status-info" iconBgColor="bg-status-info/10" />
        <MetricCard icon={Percent} value={isLoading || !kpis ? "…" : money(kpis.discounts_total)} label="Discounts given" iconColor="text-status-warning" iconBgColor="bg-status-warning/10" />
      </div>

      {isLoading && (
        <div className="space-y-4 mb-6">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      )}

      {/* ACTION PLAN */}
      {!isLoading && plays.length > 0 && (
        <Card className="mb-6 border-brand-green/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-brand-red" />
              Your action plan, ranked by impact ÷ effort
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Built live from the numbers below. Open a play for the exact steps and how to make sure it works.
            </p>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {plays.map((play) => (
                <AccordionItem key={play.rank} value={`play-${play.rank}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <span className={`flex-none w-8 h-8 rounded-lg text-brand-white font-extrabold grid place-items-center ${play.rank === 1 ? "bg-brand-red" : "bg-brand-green"}`}>
                        {play.rank}
                      </span>
                      <div>
                        <div className="font-semibold text-sm sm:text-base">{play.title}</div>
                        <div className="text-xs text-muted-foreground font-normal">{play.impact}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-sm">{play.why}</p>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-brand-gray mb-1.5">What to do</div>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                          {play.steps.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ol>
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-brand-gray mb-1.5">How to make sure it works</div>
                        <ul className="space-y-1 text-sm">
                          {play.success.map((s, i) => (
                            <li key={i} className="flex gap-2">
                              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none text-brand-green" />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {!isLoading && data && (
        <div className="space-y-6">
          {/* ACQUISITION */}
          <SectionCard
            title="New vs returning customers"
            subtitle="Is the top of your funnel growing or drying up?"
          >
            {verdict && (
              <div
                className={`mb-4 rounded-lg border px-4 py-3 text-sm flex items-start gap-2 ${
                  verdict.direction === "down"
                    ? "border-status-danger/40 bg-status-danger/10"
                    : verdict.direction === "up"
                      ? "border-status-success/40 bg-status-success/10"
                      : "border-border bg-muted"
                }`}
              >
                {verdict.direction === "down" ? (
                  <TrendingDown className="w-4 h-4 mt-0.5 text-brand-red flex-none" />
                ) : (
                  <TrendingUp className="w-4 h-4 mt-0.5 text-brand-green flex-none" />
                )}
                <span>
                  New customers {verdict.direction === "down" ? "fell" : verdict.direction === "up" ? "grew" : "held steady at"}{" "}
                  <strong>{Math.abs(verdict.changePct)}%</strong> in the second half of this period (
                  {verdict.firstHalfNew.toLocaleString()} → {verdict.secondHalfNew.toLocaleString()}).{" "}
                  {verdict.direction === "down"
                    ? "Your returning base is loyal: the leak is acquisition. Reviews, local ads, and referral offers refill the top."
                    : "Keep feeding the funnel and convert these first-timers with the 2nd-order play."}
                </span>
              </div>
            )}
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="new_customers" name="New" stroke={RED} fill={RED} fillOpacity={0.15} strokeWidth={2.5} />
                  <Area type="monotone" dataKey="returning_customers" name="Returning" stroke={GREEN} fill={GREEN} fillOpacity={0.15} strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          {/* RETENTION LADDER */}
          <SectionCard
            title="Customer value ladder (lifetime)"
            subtitle="Every extra order multiplies what a customer is worth"
          >
            <div className="grid lg:grid-cols-2 gap-6 items-center">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ladder} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v: number) => money(v)} />
                    <Bar dataKey="avg_ltv" name="Avg lifetime value">
                      <LabelList dataKey="avg_ltv" position="top" formatter={(v: number) => money(v)} fontSize={11} />
                      {ladder.map((_, i) => (
                        <Cell key={i} fill={LADDER_COLORS[i % LADDER_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-3">
                {ladder.map((l) => (
                  <StatBox
                    key={l.frequency_segment}
                    big={pctOf(Number(l.revenue), ladderRevenue)}
                    accent={l.frequency_segment === "superfan" ? "red" : "green"}
                    caption={
                      <>
                        of revenue from <b>{Number(l.customers).toLocaleString()}</b> customers with {l.label}
                      </>
                    }
                  />
                ))}
              </div>
            </div>
            <ActionNote>
              <b>Action:</b> the cheapest growth you have is turning one-time buyers into 2-time buyers, that's Play #1 above.
            </ActionNote>
          </SectionCard>

          {/* WIN-BACK */}
          <SectionCard
            title="Customers by time since last order"
            subtitle="The win-back goldmine: these people already chose you once"
          >
            <div className="grid lg:grid-cols-2 gap-6 items-center">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={recency} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="customers" name="Customers">
                      <LabelList dataKey="customers" position="top" fontSize={11} />
                      {recency.map((_, i) => (
                        <Cell key={i} fill={RECENCY_COLORS[i % RECENCY_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                <StatBox big={money(winbackValue)} caption={<>lifetime spend sitting in the cooling / at-risk / lapsing segments</>} />
                <Button
                  onClick={() => navigate("/revenue-plays?tab=winback")}
                  className="bg-brand-green hover:bg-brand-green/90 text-brand-white flex items-center gap-2"
                >
                  Get the win-back phone list
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <p className="text-xs text-muted-foreground">
                  Revenue Plays has every name, phone number, and favorite item. Export the CSV and send the SMS.
                </p>
              </div>
            </div>
          </SectionCard>

          {/* CHANNELS */}
          <SectionCard title="Average order value by channel" subtitle="Where the big baskets come from in this period">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.channels} layout="vertical" margin={{ top: 8, right: 40, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis type="category" dataKey="channel" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => money2(v)} />
                  <Bar dataKey="avg_order_value" name="Avg order value" fill={GREEN}>
                    <LabelList dataKey="avg_order_value" position="right" formatter={(v: number) => money2(v)} fontSize={11} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {data.channels.map((c) => (
                <Badge key={c.channel} variant="secondary" className="mr-2 mb-2">
                  {c.channel}: {Number(c.orders).toLocaleString()} orders · {money(c.revenue)}
                </Badge>
              ))}
            </div>
            <ActionNote>
              <b>Action:</b> promote delivery to your carryout base: "Free delivery over $40" turns small baskets into big ones.
            </ActionNote>
          </SectionCard>

          {/* DISCOUNTS */}
          {discounts && (
            <SectionCard
              title="Discount spend"
              subtitle="Are discounts buying you new customers, or paying people who'd order anyway?"
            >
              <div className="flex flex-wrap gap-3">
                <StatBox big={money(discounts.total_discount)} caption={<>given away on <b>{Number(discounts.discounted_orders).toLocaleString()}</b> orders ({pctOf(Number(discounts.discounted_orders), Number(discounts.paid_orders))} of all paid orders)</>} />
                <StatBox big={money(discounts.discount_to_repeat)} caption={<>went to <b>repeat</b> customers who were likely coming back anyway</>} />
                <StatBox big={`≈ ${money(Number(discounts.discount_to_repeat) / 3)}`} accent="green" caption={<>recoverable margin from smarter targeting (reclaim a third)</>} />
              </div>
              {discounts.avg_basket_discounted != null && discounts.avg_basket_full != null && (
                <p className="text-sm mt-4 text-muted-foreground">
                  Discounted orders do carry bigger baskets ({money2(discounts.avg_basket_discounted)} vs{" "}
                  {money2(discounts.avg_basket_full)} subtotal), but the discount usually eats more than the uplift.
                </p>
              )}
              <ActionNote>
                <b>Action:</b> switch to threshold offers ("$5 off $35+") and aim discounts at acquisition + win-back, not regulars. Play #
                {plays.find((p) => p.title.includes("discount"))?.rank ?? 3} above has the steps.
              </ActionNote>
            </SectionCard>
          )}

          {/* DAY OF WEEK */}
          <SectionCard title="Revenue by day of week" subtitle="Weekends carry the load: the soft days are your promo targets">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dowData} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => money(v)} />
                  <Bar dataKey="revenue" name="Revenue">
                    {dowData.map((d) => (
                      <Cell key={d.iso_dow} fill={weak.includes(d.label) ? YELLOW : GREEN} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {weak.length === 2 && (
              <ActionNote>
                <b>Action:</b> {weak.join(" and ")} are your softest days (highlighted): run a day-locked offer to fill the ovens without
                discounting the weekend rush.
              </ActionNote>
            )}
          </SectionCard>

          {/* PAIRINGS */}
          {data.pairings.length > 0 && (
            <SectionCard title="What sells together" subtitle="Proven combos from this period's orders: your upsell shortlist">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHead label="Anchor item" sortKey="item_a" sort={pairSort} onToggle={togglePairSort} />
                      <SortableHead label="Frequently bought with" sortKey="item_b" sort={pairSort} onToggle={togglePairSort} />
                      <SortableHead label="Times paired" sortKey="times_paired" sort={pairSort} onToggle={togglePairSort} align="right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPairings.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{p.item_a}</TableCell>
                        <TableCell>{p.item_b}</TableCell>
                        <TableCell className="text-right tabular-nums">{Number(p.times_paired).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ActionNote>
                <b>Action:</b> train staff to offer the matching side on every phone order, and add the top combo to your
                order-confirmation SMS as a one-tap add-on suggestion.
              </ActionNote>
            </SectionCard>
          )}

          {/* LOYALTY */}
          {members && nonMembers && (
            <SectionCard
              title="Loyalty program check (lifetime)"
              subtitle="A loyalty program should make members order MORE than non-members"
            >
              <div className="flex flex-wrap gap-3">
                <StatBox
                  big={`${Number(members.avg_orders)} vs ${Number(nonMembers.avg_orders)}`}
                  accent={Number(members.avg_orders) >= Number(nonMembers.avg_orders) ? "green" : "red"}
                  caption={<>avg orders, members vs non-members ({Number(members.customers).toLocaleString()} members)</>}
                />
                <StatBox
                  big={`${money(members.avg_ltv)} vs ${money(nonMembers.avg_ltv)}`}
                  accent={Number(members.avg_ltv) >= Number(nonMembers.avg_ltv) ? "green" : "red"}
                  caption={<>avg lifetime value, members vs non-members</>}
                />
              </div>
              <ActionNote>
                {Number(members.avg_ltv) >= Number(nonMembers.avg_ltv) ? (
                  <>
                    <b>Good news:</b> members out-spend non-members. Keep enrolling your best customers and add a frequency reward
                    (every 5th order = free side) to widen the gap.
                  </>
                ) : (
                  <>
                    <b>Action:</b> members spend less than non-members: the program isn't changing behavior. Rebuild it around
                    frequency rewards (every 5th order = free side) and auto-enroll high-value customers.
                  </>
                )}
              </ActionNote>
            </SectionCard>
          )}

          <p className="text-center text-xs text-muted-foreground pt-2">
            Figures use orders with captured payment ({format(from, "MMM d, yyyy")} – {format(to, "MMM d, yyyy")}). Customer value
            ladder, win-back segments, and loyalty check reflect all-time history.
          </p>
        </div>
      )}
    </div>
  );
};

export default Insights;
